/**
 * /dashboard/posh/reports — Annual report list (HR-admin generates).
 *
 * Migration note (Phase 3f — 2026-06-01): /dashboard/posh/reports/$id is
 * ported; <a href> swapped for typed Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useReports } from "@/hooks/use-posh";
import { fmtDate } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { ReportGenerateForm } from "@/components/posh/report-generate-form";
import { usePoshAdmin } from "@/components/posh/use-posh-admin";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/reports/")({
  component: ReportsRoute,
});

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ReportList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const q = useReports();
  if (q.isLoading) return <ListSkeleton />;
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load reports</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }
  const reports = q.data?.reports ?? [];
  if (reports.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          No annual reports yet. Generate one for a calendar year (POSH
          Act s.21).
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {[...reports]
        .sort((a, b) => b.year - a.year)
        .map((r) => (
          <Link
            key={r.id}
            to="/app/$tenantSlug/posh/reports/$id"
            params={{tenantSlug, id: String(r.id)}}
            className="group block"
          >
            <Card className="transition-colors group-hover:border-primary/40">
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      Annual report {r.year}
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {r.filing_deadline
                        ? `Filing deadline ${fmtDate(r.filing_deadline)}`
                        : "Statutory annual report"}
                      {r.filed_at
                        ? ` · Filed ${fmtDate(r.filed_at)}`
                        : ""}
                    </p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              </CardHeader>
            </Card>
          </Link>
        ))}
    </div>
  );
}

function ReportsRoute() {
  const isAdmin = usePoshAdmin();
  return (
    <PoshModuleShell
      title="Annual Reports"
      description="Statutory POSH annual reports (counts only — no complainant identities). File with the district officer by 31 January."
      actions={isAdmin ? <ReportGenerateForm /> : undefined}
    >
      <PoshFeatureGate>
        <ReportList />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
